import { Api } from '../../Api/Api';
import { AppDispatch, RootState } from '../Store';
import { abortableSleep } from '../../Utility/Utils';
import { createTracedAsyncThunk } from '../../../tracing/trace';
import moment from 'moment';
import { anySignal } from '../../Utility/anySignal';
import { getAbortReasonFallback } from '../../Utility/registerCancelableThunks';

export interface IPpgRequest
{
	HybridIds: string[];
	AttributeIds: string[];
}

type DownloadPlanPdfArgs = IPpgRequest & { __externalSignal?: AbortSignal };

export const downloadPpgPdf = createTracedAsyncThunk<Blob, DownloadPlanPdfArgs, { dispatch: AppDispatch, state: RootState }>(
	'ppg/download',
	async (context, request: DownloadPlanPdfArgs, thunkAPI) =>
	{
		const { getState, rejectWithValue, signal: rtkSignal, requestId } = thunkAPI;

		// ⟵ build the merged signal the thunk will use everywhere
		const combined = anySignal([rtkSignal, request.__externalSignal]);

		try
		{
			const { auth } = getState();
			const api = new Api('/api/6', auth.userAuthToken, context);

			const pdfSession = await api.postAsync<string>('pdf/ppg/async', request ?? {}, { signal: combined });

			let pdfData = undefined;
			do 
			{
				// wait half a second for each look
				await abortableSleep(500, combined);
				pdfData = await api.getFileAsync(`pdf/ppg/${pdfSession.Data}`, { signal: combined });
			} while(!pdfData);

			const { name, data } = pdfData;

			if (!data.size)
			{
				return rejectWithValue('There was an error downloading the PPG PDF data.');
			}

			if (window.navigator.userAgent.toLowerCase().indexOf('crios') > -1)
			{//iOS Chrome
				const typedBlob = new Blob([data], {type: 'application/pdf'});
				const link = (window.URL || window['webkitURL']).createObjectURL(typedBlob);
				window.open(link);
			}
			else
			{
				// Store this file as a hidden 'a' element and then automatically click it.
				const link = document.createElement('a');
				link.href = (window.URL || window['webkitURL']).createObjectURL(data);

				// The renderer always adds ".pdf" to the filename. If there's more than that, use it. Otherwise fall back on the session name.
				link.download = name && name.slice(0, name.indexOf('.pdf'))
					? name : `ProductGuide_${moment().format('MM/DD/YYYY')}`;

				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			if (combined.aborted)
			{
				const r = (combined as any).reason ?? getAbortReasonFallback(requestId) ?? new DOMException('Aborted', 'AbortError');
				const msg = typeof r === 'object' && r?.message ? r.message : String(r);
				return rejectWithValue(msg);
			}
			return rejectWithValue(e.message);
		}
	}
);
